import React, {createContext, useContext, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {OverlayTrigger, Popover} from 'react-bootstrap';
import {getCalendarMediaStatus} from '../api';

export const MediaStatusContext = createContext({statuses: {}, error: false});
export const states = {
  complete: ['#16803c', 'Video completato'],
  partial: ['#b86200', 'Video parziale'],
  failed: ['#c62828', 'Elaborazione fallita'],
  processing: ['#1565c0', 'Elaborazione / pubblicazione in corso'],
  waiting: ['#1565c0', 'In attesa / nuovo tentativo'],
  available: ['#62676d', 'Video disponibile; completezza non certificata'],
  unknown: ['#62676d', 'Stato video non disponibile'],
  cancelled: ['#62676d', 'Elaborazione annullata']
};

export function CalendarMediaStatusProvider({slots, token, children}) {
  const ids = [...new Set(slots.map(s => Number(s.slotId)).filter(n => Number.isInteger(n) && n > 0))].sort((a, b) => a - b).join(',');
  const [value, setValue] = useState({statuses: {}, error: false});
  useEffect(() => {
    let active = true;
    let timer;
    setValue({statuses: {}, error: false});
    if (!ids || !token) return undefined;
    const load = async () => {
      const list = ids.split(',');
      const statuses = {};
      let error = false;
      // Bounded batch requests, never a separate request/timer for every event.
      for (let index = 0; index < list.length && active; index += 100) {
        const response = await getCalendarMediaStatus(list.slice(index, index + 100), token);
        if (response.status !== 200) { error = true; break; }
        Object.assign(statuses, response.json.slots);
      }
      if (!active) return;
      setValue({statuses, error});
      timer = setTimeout(load, 30000);
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [ids, token]);
  return <MediaStatusContext.Provider value={value}>
    <div style={{width: '100%', fontSize: 12, marginBottom: 6}} aria-label="Legenda stato video">
      Stato video: {['complete', 'partial', 'failed', 'processing', 'unknown'].map(status => <span key={status} style={{marginRight: 12}}>
        <span aria-hidden="true" style={{color: states[status][0]}}>●</span> {states[status][1]}
      </span>)}
    </div>
    {children}
  </MediaStatusContext.Provider>;
}

export function CalendarMediaDot({slot}) {
  const {statuses, error} = useContext(MediaStatusContext);
  const result = statuses[slot.slotId];
  if ((!result && (!error || !(slot.selectedCameras || []).length)) || (result && result.status === 'not_requested')) return null;
  const [color, label] = states[error ? 'unknown' : result.status] || states.unknown;
  const stop = event => event.stopPropagation();
  return <OverlayTrigger placement="auto" trigger={['hover', 'focus', 'click']} rootClose
    overlay={<Popover id={`media-status-${slot.slotId}`} onClick={stop} onDoubleClick={stop}>
      <Popover.Title as="h3">Evento {slot.slotId}: {label}</Popover.Title>
      <Popover.Content>
        {error ? 'Aggiornamento dello stato non riuscito. Nuovo tentativo automatico entro 30 secondi.'
          : <ul style={{paddingLeft: 16, marginBottom: 0}}>{result.cameras.map(camera => <li key={camera.cameraId}>
            <strong>{camera.name} ({camera.cameraId})</strong>: {(states[camera.status] || states.unknown)[1]}.
            {camera.videoAvailable && <> Video disponibile: {Math.round(camera.durationSeconds)} s.</>}
            {camera.missingSeconds > 0 && <> Intervalli mancanti, avvio incluso: {camera.missingSeconds} s.</>}
            {camera.trackingUnavailable && <> Regia automatica non disponibile.</>}
            {camera.errorCode && <> {camera.errorCode}</>}
          </li>)}</ul>}
      </Popover.Content>
    </Popover>}>
    <button type="button" aria-label={`Stato video evento ${slot.slotId}: ${label}`}
      onClick={stop} onDoubleClick={stop} onMouseDown={stop} onTouchStart={stop} onKeyDown={stop}
      style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: 24, height: 24, padding: 4, marginRight: 5, border: 0, borderRadius: '50%',
        background: 'transparent', cursor: 'pointer'}}>
      <span aria-hidden="true" style={{width: 12, height: 12, borderRadius: '50%', background: color,
        border: '2px solid white', boxShadow: '0 0 0 1px #333', display: 'block'}}/>
    </button>
  </OverlayTrigger>;
}

export default connect(({login: {token}}) => ({token}))(CalendarMediaStatusProvider);
